/**
 * Descriptor pricing: rate evaluation, rounding, the legacy resolution ladder, the admin
 * override, the margin policy and the published registry rate.
 *
 * PURE: `import type` only. Importable from server/ and src/ (the client quote uses it).
 *
 * `legacy-resolution-v1` (RESOLUTION_MULTIPLIERS / getResolutionTier / getResolutionMultiplier)
 * was MOVED here verbatim from CreditService.ts:936-1000 (Phase 1 U3). CreditService imports it,
 * so the generic tail and the descriptor `ladder` rate read the SAME function and cannot drift.
 * generation/legacyResolutionParity.test pins it to a frozen copy of the 9089a33 original.
 * Do not "fix" anything here (the 1440p -> '1080p' tier, the includes() matching): it is a
 * parity replay, and FLUX-class SKUs bill through it.
 *
 * INTEGRATION (p1/integration): U1 and U3 each shipped a pricing.ts. This is the single merged
 * module: U1's rate/rounding API, U3's moved ladder names (CreditService imports them), U3's
 * exact-thousandths override, U3's flatRate rule for the ladder, policyCredits and publishedRate.
 * The one priceSku lives in engine.ts; U3's parallel priceSku.ts was deleted.
 */
import type { Pricing, Rate, Scalar, Sku } from './types';

// ---------------------------------------------------------------------------
// legacy-resolution-v1 (moved from CreditService.ts:936-1000, byte-for-byte behaviour)
// ---------------------------------------------------------------------------

/**
 * Resolution tier multipliers for credit calculation.
 * Matches frontend creditCostUtils.ts.
 */
export const RESOLUTION_MULTIPLIERS: Readonly<Record<string, number>> = Object.freeze({
  '480p': 0.75,  // Lower than base for Wan Animate
  '580p': 1,     // Wan Animate base resolution
  '720p': 1,
  '1080p': 1.5,
  '1440p': 2,
  '4k': 3,
});

/** U1's name for the same table (descriptor code and its tests use it). */
export const LEGACY_RESOLUTION_MULTIPLIERS = RESOLUTION_MULTIPLIERS;

/**
 * Convert resolution string to tier
 */
export function getResolutionTier(resolution: string): string {
  // Special resolutions for Wan Animate (must check before general video resolutions)
  if (resolution === '480p' || resolution.includes('480')) {
    return '480p';
  }
  if (resolution === '580p' || resolution.includes('580')) {
    return '580p';
  }

  // Video resolutions
  if (resolution === '720p' || resolution.includes('720') || resolution.includes('1280x720') || resolution.includes('720x1280')) {
    return '720p';
  }
  if (resolution === '1080p' || resolution.includes('1080') || resolution.includes('1920x1080') || resolution.includes('1080x1920')) {
    return '1080p';
  }
  if (resolution === '1440p' || resolution.includes('1440') || resolution.includes('2560x1440') || resolution.includes('1440x2560') || resolution.includes('1792x1024') || resolution.includes('1024x1792')) {
    return '1080p'; // 1792x1024 is roughly 1080p equivalent
  }
  if (resolution === '4k' || resolution.includes('4k') || resolution.includes('4K') || resolution.includes('3840') || resolution.includes('2160')) {
    return '4k';
  }

  // Image resolutions (by pixel size)
  if (resolution.includes('512') || resolution === '512px') {
    return '720p'; // Base tier
  }
  if (resolution.includes('1024') || resolution === '1024px') {
    return '1080p';
  }
  if (resolution.includes('2048') || resolution === '2048px') {
    return '1440p';
  }
  if (resolution.includes('4096') || resolution === '4096px') {
    return '4k';
  }

  // Default to base resolution
  return '720p';
}

/** U1's name for the same function. */
export const legacyResolutionTier = getResolutionTier;

/**
 * CreditService.getResolutionMultiplier, verbatim (falsy resolution -> 1). Like the original,
 * a non-string truthy value throws inside `.includes`; CreditService keeps that behaviour.
 */
export function getResolutionMultiplier(resolution?: string): number {
  if (!resolution) return 1;
  const tier = getResolutionTier(resolution);
  return RESOLUTION_MULTIPLIERS[tier] || 1;
}

/**
 * getResolutionMultiplier made TOTAL for the descriptor path: a non-string truthy value
 * (which would throw inside the original's `.includes`) is String()-ed.
 *
 * KNOWN EDGE DIVERGENCE (credits): a numeric resolution on a ladder axis, e.g. a headless body
 * sending FLUX `resolution: 1080`, bills x1.5 here while legacy throws inside reserve() and the
 * request 500s WITHOUT charging. The client quote needs this to stay total, so it is not
 * "fixed"; U2's golden classifier must file it as B1 (legacy failed before reserving) rather
 * than a credit difference. Pinned by legacyResolutionParity.test ('stays total ...').
 */
export function legacyResolutionMultiplier(resolution?: unknown): number {
  if (!resolution) return 1;
  return getResolutionMultiplier(typeof resolution === 'string' ? resolution : String(resolution));
}

// ---------------------------------------------------------------------------
// Rates and rounding
// ---------------------------------------------------------------------------

export interface RateEval {
  /** Credits per unit before rounding. */
  rate: number;
  /** Ladder multiplier (1 unless the rate is a legacy ladder on a non-flatRate SKU). */
  multiplier: number;
  /** Table key used ('' for flat; the legacy tier for a ladder). */
  tierKey: string;
}

/**
 * Evaluates a SKU rate against resolved params. Total: an unknown table key uses fallbackKey.
 * `flatRate` mirrors model_registry.flat_rate: CreditService's generic tail skips the ladder
 * multiplier for a flat-rate row (`model.flatRate ? 1 : getResolutionMultiplier(resolution)`),
 * so a ladder SKU whose registry row is flat bills the base rate at every resolution.
 */
export function evaluateRate(
  rate: Rate,
  params: Readonly<Record<string, Scalar | null | undefined>>,
  flatRate = false,
): RateEval {
  if ('flat' in rate) return { rate: rate.flat, multiplier: 1, tierKey: '' };
  if ('table' in rate) {
    const key = rate.by.map((p) => {
      const v = params[p];
      return v === null || v === undefined ? '' : String(v);
    }).join('|');
    if (Object.prototype.hasOwnProperty.call(rate.table, key)) {
      return { rate: rate.table[key], multiplier: 1, tierKey: key };
    }
    return { rate: rate.table[rate.fallbackKey], multiplier: 1, tierKey: rate.fallbackKey };
  }
  const axis = params[rate.ladder.by];
  const tierKey = axis ? getResolutionTier(typeof axis === 'string' ? axis : String(axis)) : '';
  return { rate: rate.ladder.base, multiplier: flatRate ? 1 : legacyResolutionMultiplier(axis), tierKey };
}

/**
 * Applies the pricing rounding mode to a unit count.
 *   'table-min1': rate * max(1, ceil(u))                 (the explicit tier-table branches)
 *   'generic':    ceil(rate * ceil(u) * multiplier)      (CreditService's generic tail)
 * No epsilon: these reproduce legacy float arithmetic exactly.
 */
export function roundCredits(rounding: Pricing['rounding'], ev: RateEval, units: number): number {
  if (rounding === 'table-min1') {
    return Math.ceil(ev.rate * ev.multiplier * Math.max(1, Math.ceil(units)));
  }
  return Math.ceil(ev.rate * Math.ceil(units) * ev.multiplier);
}

// ---------------------------------------------------------------------------
// Admin override (R12, migration 143 price_override_multiplier numeric(5,3))
// ---------------------------------------------------------------------------

/**
 * `ceil(credits × m)`, computed in integer thousandths: `m` is a decimal the DB stores exactly,
 * but its double is not (1.1 is 1.1000000000000000888…), so a bare `Math.ceil(50 * 1.1)` bills
 * 56 for an exact 55. `credits × round(m × 1000)` is an exact integer and dividing it by 1000
 * is exact whenever the true product is an integer, so the ceil only rounds up real fractions.
 * An absent, non-finite, non-positive or exactly-1 multiplier leaves the price untouched.
 */
export function applyOverride(credits: number, multiplier?: number | null): number {
  if (multiplier == null || !Number.isFinite(multiplier) || multiplier <= 0) return credits;
  const milli = Math.round(multiplier * 1000);
  if (milli === 1000) return credits;
  return Math.ceil((credits * milli) / 1000);
}

/** U3's name for the same function (CreditService, the client quote and override.test use it). */
export const applyOverrideMultiplier = applyOverride;

// ---------------------------------------------------------------------------
// Margin policy and the published registry rate (U3: pricingPolicy, projection, validator)
// ---------------------------------------------------------------------------

/**
 * The house margin policies (CLAUDE.md: 1 credit = $0.00333, ~35% gross margin):
 *   'fal-video-35' / 'fal-image-35' : credits = ceil(usd × 300 / 0.65)
 * (the formula every fal migration header quotes, e.g. 111/112/118/127/128).
 * The epsilon absorbs binary float error (0.13 × 300 / 0.65 = 60.000000000000014).
 * A custom policy has no formula here: returns undefined, and pricingPolicy.test requires a
 * marginException for it.
 */
export function policyCredits(policy: Pricing['policy'], usd: number): number | undefined {
  if (policy === 'fal-video-35' || policy === 'fal-image-35') return Math.ceil((usd * 300) / 0.65 - 1e-9);
  return undefined;
}

/**
 * The `credits_per_unit` a SKU's model_registry row carries: the rate at `publishedTier`
 * (table), the flat rate, or the ladder base. Used by the projection generator and the
 * startup validator.
 */
export function publishedRate(sku: Sku): number {
  const rate = sku.rate;
  if ('flat' in rate) return rate.flat;
  if ('ladder' in rate) return rate.ladder.base;
  const key = sku.registry.publishedTier ?? rate.fallbackKey;
  return rate.table[key] ?? rate.table[rate.fallbackKey];
}
